import { useEffect, useState } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import {
  Card,
  Tabs,
  Descriptions,
  Table,
  Tag,
  Spin,
  message,
  Button,
  Modal,
  Form,
  InputNumber,
  Select,
} from "antd";
import {
  getWorkspaceApi,
  getWorkspaceMembersApi,
  getWorkspaceInvitationsApi,
  getWorkspacePropertiesApi,
  getWorkspaceIntegrationsApi,
  getWorkspaceAuditEventsApi,
  assignWorkspacePlanApi,
  grantWorkspaceQuotaApi,
  suspendWorkspaceApi,
  activateWorkspaceApi,
} from "../api/admin.workspaces.api";
import { get_subscription_plansApi } from "../api/admin.billing.apis";
import { meApi } from "../api/auth.api";
import WorkspaceDeleteModal from "../components/workspaces/WorkspaceDeleteModal";

function WorkspaceDetail() {
  const { workspaceId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planModal, setPlanModal] = useState(false);
  const [quotaModal, setQuotaModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [planForm] = Form.useForm();
  const [quotaForm] = Form.useForm();
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const role = currentUser?.user?.role;
  const isSuperAdmin = role === "super_admin";
  const canManageLifecycle = role === "admin" || role === "super_admin";
  const lifecycleSuspended =
    workspace?.lifecycle_status === "suspended" ||
    workspace?.lifecycle_status === "archived";

  const load = async () => {
    try {
      setLoading(true);
      const [wsRes, memRes, invRes, propRes, intRes, audRes] = await Promise.all([
        getWorkspaceApi(workspaceId),
        getWorkspaceMembersApi(workspaceId),
        getWorkspaceInvitationsApi(workspaceId),
        getWorkspacePropertiesApi(workspaceId),
        getWorkspaceIntegrationsApi(workspaceId),
        getWorkspaceAuditEventsApi(workspaceId, { page_size: 30 }),
      ]);
      if (wsRes.data.status === "success") {
        setWorkspace(wsRes.data.data?.workspace);
      }
      if (memRes.data.status === "success") setMembers(memRes.data.data?.members || []);
      if (invRes.data.status === "success") setInvitations(invRes.data.data?.invitations || []);
      if (propRes.data.status === "success") setProperties(propRes.data.data?.properties || []);
      if (intRes.data.status === "success") setIntegrations(intRes.data.data);
      if (audRes.data.status === "success") {
        setAuditEvents((audRes.data.data?.events || []).map((e, i) => ({ ...e, key: e._id || i })));
      }
    } catch (e) {
      message.error("Failed to load workspace");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    meApi()
      .then((res) => {
        if (res.data.status === "success") setCurrentUser(res.data.data);
      })
      .catch(() => {});
    get_subscription_plansApi(true).then((res) => {
      if (res.data.status === "success") {
        setPlans(res.data.data?.plans || []);
      }
    });
  }, [workspaceId]);

  const handleAssignPlan = async (values) => {
    setActionLoading(true);
    try {
      const res = await assignWorkspacePlanApi(workspaceId, { plan_id: values.plan_id });
      if (res.data.status === "success") {
        message.success("Plan assigned to workspace owner");
        setPlanModal(false);
        load();
      } else {
        message.error(res.data.message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleLifecycle = async () => {
    setActionLoading(true);
    try {
      const res = lifecycleSuspended
        ? await activateWorkspaceApi(workspaceId)
        : await suspendWorkspaceApi(workspaceId);
      if (res.data.status === "success") {
        message.success(res.data.message || "Updated");
        load();
      } else {
        message.error(res.data.message || "Failed to update workspace");
      }
    } catch (e) {
      message.error("Failed to update workspace");
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuotaGrant = async (values) => {
    setActionLoading(true);
    try {
      const res = await grantWorkspaceQuotaApi(workspaceId, values);
      if (res.data.status === "success") {
        message.success("Quota grant applied");
        setQuotaModal(false);
        load();
      } else {
        message.error(res.data.message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !workspace) {
    return <Spin style={{ display: "block", margin: "48px auto" }} />;
  }
  if (!workspace) {
    return <Card>Workspace not found. <Link to="/workspaces">Back</Link></Card>;
  }

  const sub = workspace.subscription || {};
  const usage = sub.usage || {};

  return (
    <div className="layout-content">
      <Card
        title={workspace.name}
        extra={
          <>
            <Button style={{ marginRight: 8 }} onClick={() => setQuotaModal(true)}>
              Quota grant
            </Button>
            <Button type="primary" onClick={() => setPlanModal(true)}>
              Change plan
            </Button>
            {canManageLifecycle && (
              <Button
                style={{ marginLeft: 8 }}
                loading={actionLoading}
                onClick={handleLifecycle}
              >
                {lifecycleSuspended ? "Activate" : "Suspend"}
              </Button>
            )}
            {isSuperAdmin && (
              <Button danger style={{ marginLeft: 8 }} onClick={() => setDeleteOpen(true)}>
                Delete workspace
              </Button>
            )}
          </>
        }
      >
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Workspace ID">{workspace.workspace_id}</Descriptions.Item>
          <Descriptions.Item label="Owner">{workspace.owner_email}</Descriptions.Item>
          <Descriptions.Item label="Plan">{workspace.plan_name || "—"}</Descriptions.Item>
          <Descriptions.Item label="Subscription">{workspace.subscription_status || "—"}</Descriptions.Item>
          <Descriptions.Item label="Lifecycle">
            {lifecycleSuspended ? (
              <Tag color="red">suspended</Tag>
            ) : (
              <Tag color="green">{workspace.lifecycle_status || "active"}</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Onboarding">{workspace.onboarding_status}</Descriptions.Item>
          <Descriptions.Item label="Seats">
            {workspace.seats_used}
            {workspace.seats_limit != null ? ` / ${workspace.seats_limit}` : ""}
          </Descriptions.Item>
          <Descriptions.Item label="Properties active">{workspace.properties_active}</Descriptions.Item>
          <Descriptions.Item label="MRR">
            {typeof workspace.mrr === "number" && workspace.mrr > 0 && workspace.mrr_currency
              ? `${workspace.mrr_currency} ${workspace.mrr}`
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Quota used">
            {usage.general_quota_used ?? "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs style={{ marginTop: 16 }} defaultActiveKey="members">
        <Tabs.TabPane tab="Members" key="members">
          <Table
            dataSource={members.map((m, i) => ({ ...m, key: m.membership_id || i }))}
            columns={[
              { title: "Email", dataIndex: "email" },
              { title: "Role", dataIndex: "role" },
              { title: "Status", dataIndex: "status" },
              {
                title: "Entitlement",
                dataIndex: "entitlement_status",
                render: (v) => <Tag>{v || "active"}</Tag>,
              },
            ]}
            pagination={false}
            size="small"
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Invitations" key="invitations">
          <Table
            dataSource={invitations.map((inv, i) => ({ ...inv, key: inv._id || i }))}
            columns={[
              { title: "Email", dataIndex: "email" },
              { title: "Role", dataIndex: "role" },
              { title: "Status", dataIndex: "status" },
              { title: "Expires", dataIndex: "expires_at" },
            ]}
            pagination={false}
            size="small"
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Properties" key="properties">
          <Table
            dataSource={properties.map((p) => ({ ...p, key: p.property_id }))}
            columns={[
              { title: "Name", dataIndex: "name" },
              { title: "Website", dataIndex: "website_url" },
              { title: "GA4 id", dataIndex: "ga4_property_id" },
              { title: "Lifecycle", dataIndex: "lifecycle_status" },
              { title: "Entitlement", dataIndex: "entitlement_status" },
              { title: "Replay", dataIndex: "replay_status" },
            ]}
            pagination={false}
            size="small"
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Integrations" key="integrations">
          <pre style={{ fontSize: 12, maxHeight: 400, overflow: "auto" }}>
            {JSON.stringify(integrations, null, 2)}
          </pre>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Audit" key="audit">
          <Table
            dataSource={auditEvents}
            columns={[
              { title: "Type", dataIndex: "event_type" },
              { title: "Actor", dataIndex: "actor_user_id" },
              { title: "At", dataIndex: "created_at" },
            ]}
            pagination={false}
            size="small"
          />
        </Tabs.TabPane>
      </Tabs>

      <Modal title="Assign plan (owner)" open={planModal} onCancel={() => setPlanModal(false)} footer={null}>
        <Form form={planForm} onFinish={handleAssignPlan} layout="vertical">
          <Form.Item name="plan_id" label="Plan" rules={[{ required: true }]}>
            <Select placeholder="Select plan">
              {plans.map((p) => (
                <Select.Option key={p._id || p.key} value={p._id || p.key}>
                  {p.name || p.key}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={actionLoading}>
            Assign
          </Button>
        </Form>
      </Modal>

      <WorkspaceDeleteModal
        open={deleteOpen}
        workspaceId={workspaceId}
        onCancel={() => setDeleteOpen(false)}
        onDeleted={() => history.push("/workspaces")}
      />

      <Modal title="Quota grant" open={quotaModal} onCancel={() => setQuotaModal(false)} footer={null}>
        <Form form={quotaForm} onFinish={handleQuotaGrant} layout="vertical">
          <Form.Item name="extended_quota_limit" label="Extended quota limit" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={actionLoading}>
            Apply
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

export default WorkspaceDetail;
